import './styles/warranty.scss';

import React, { useEffect } from 'react';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import ReactMarkdown from 'react-markdown';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { TitleBanner } from '../components/Primitives';
import arrowRight from '../images/icons/droletArrowRight.svg';
import arrowRightOrange from '../images/icons/droletArrowRightOrange.svg';
import downloadIcon from '@progymedia/sbi-web-drolet/src/images/icons/download-icon.svg';
import gfm from 'remark-gfm';
import { graphql } from 'gatsby';
import manualIcon from '@progymedia/sbi-web-drolet/src/images/icons/manual-icon.svg';
import { useLocation } from '@reach/router';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($type: String!) {
        warranty: allProductsJson(filter: { code: { eq: $type } }) {
            nodes {
                ...cmsDataFragment
                documents {
                    en_CA
                    en_US
                    fr_CA
                    caption {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Warranty({ data, location }) {
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const { t } = useTranslation('pages');
    const warrantyCMS = data.warranty.nodes[0];
    const warrantyAttributes = warrantyCMS?.attributes;
    const description =
        warrantyCMS?.description?.valuesMarkdownRemark?.[currentLocale];
    const table = warrantyAttributes?.content?.values?.[0]?.[currentLocale];
    const doc = warrantyCMS?.documents?.[0];

    const type = useLocation().pathname;
    const isCurrentType = (pageType: string): boolean => {
        return type.includes(pageType);
    };

    const isMobile = useMobileDetect().isMobile();
    const responsiveImages = data?.warranty?.nodes?.[0]?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp
            ?.gatsbyImageData;

    const metadata = {
        title: warrantyAttributes?.meta_title?.values[0][currentLocale],
        description:
            warrantyAttributes?.meta_description?.values[0][currentLocale],
    };

    useEffect(() => {
        if (location && typeof window !== 'undefined')
            window.scrollTo(0, location?.state?.scrollPosition);
    }, [location, location?.state]);

    const isOtherCategory = (location.pathname as string).includes(
        t('warranty.others').toLowerCase()
    );

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <TitleBanner
                title={warrantyCMS?.title?.[currentLocale]}
                imageData={isMobile ? mobileImage : desktopImage}
                content={description}
                button={{
                    text: t('warranty.register-warranty'),
                    image: arrowRightOrange,
                }}
                className='warranty-banner'
            />
            <div className='warranty-btns'>
                <Button
                    className={
                        isCurrentType(t('url:stoves-inserts'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:stoves-inserts')) &&
                            navigateTranslated(
                                '/warranty/stoves-inserts/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.stoves-inserts')}
                />
                <Button
                    className={
                        isCurrentType(t('url:pellet-stoves'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:pellet-stoves')) &&
                            navigateTranslated(
                                '/warranty/pellet-stoves/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.pellet-stoves')}
                />
                <Button
                    className={
                        isCurrentType(t('url:wood-furnaces'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:wood-furnaces')) &&
                            navigateTranslated(
                                '/warranty/wood-furnaces/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.wood-furnaces')}
                />
                <Button
                    className={
                        isCurrentType(t('url:cook-stoves'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:cook-stoves')) &&
                            navigateTranslated(
                                '/warranty/cook-stoves/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.cook-stoves')}
                />
                <Button
                    className={
                        isCurrentType(t('url:others')) ? 'btn selected' : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:others')) &&
                            navigateTranslated(
                                '/warranty/others/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.others')}
                />
            </div>

            <div className='warranty container'>
                <ReactMarkdown remarkPlugins={[gfm]} children={table} />

                {!isOtherCategory && doc && (
                    <div className='downloadWarranty-div'>
                        <a
                            rel='noreferrer'
                            target='_blank'
                            className='downloadWarranty'
                            href={'/file/' + doc[currentLocale]}
                        >
                            <span>{t('warranty.download')}</span>
                            <img src={arrowRight} alt='' />
                        </a>
                    </div>
                )}

                {isOtherCategory && (
                    <div className='downloadOthersWarranty'>
                        {warrantyCMS?.documents?.map(
                            (document, index: number) => {
                                return (
                                    <div
                                        className='others-warranty-documents'
                                        key={`${document?.[currentLocale]}-${index}`}
                                    >
                                        <div className='document-info'>
                                            <div className='document-title'>
                                                <img src={manualIcon} alt='' />
                                                <span>
                                                    {document[
                                                        currentLocale
                                                    ].replace('.pdf', '')}
                                                </span>
                                            </div>

                                            <a
                                                rel='noreferrer'
                                                target='_blank'
                                                href={
                                                    '/file/' +
                                                    document[currentLocale]
                                                }
                                                className='document-download'
                                            >
                                                {t('layout:appliance.download')}
                                                <img
                                                    src={downloadIcon}
                                                    alt=''
                                                ></img>
                                            </a>
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
